import { ReactNode } from 'react';

type Props = {
  /** Ten pixels of travel per unit, and negative rises as the page falls. */
  speed: number;
  progress: number | null;
  /** Swells from 1 to 1.25 across the scroll, as the two ellipses did at the source. */
  zoom?: boolean;
  /** Also drifts a hundredth of its own width to the right, as `dots-2` did. */
  shiftX?: boolean;
  children: ReactNode;
};

/**
 * One drifting layer of the hero.
 *
 * The box is the part that matters and the easy part to lose. headless handed each layer to
 * react-scroll-parallax's `<ParallaxBanner>`, which lays one out at `inset: -Npx 0` — N
 * being ten pixels per unit of speed — so the box a layer's contents position against is
 * taller than the hero by N above it and N below it. Every `top-*`, `bottom-*` and
 * percentage offset inside is then measured from those extended edges, and that is what
 * holds the covers out at the rim of the screen, clear of the headline. Position the very
 * same classes against the hero itself, as this page did until now, and all twelve collapse
 * inward by 50, 100 or 150px depending on their speed — far enough that the largest of them
 * sits over "Unlimited access to..." from around 1300px down.
 *
 * The expansion is also exactly the slack the drift needs: a layer travels from N below
 * where it sits to N above it, so it never scrolls its own edge into view.
 */
export default function ParallaxLayer({ speed, progress, zoom, shiftX, children }: Props) {
  const travel = Math.abs(speed) * 10;
  const transform =
    progress === null
      ? undefined
      : [
          shiftX ? `translateX(${progress.toFixed(4)}%)` : '',
          `translate3d(0, ${(speed * 10 * (1 - 2 * progress)).toFixed(2)}px, 0)`,
          zoom ? `scale(${(1 + progress / 4).toFixed(4)})` : '',
        ]
          .filter(Boolean)
          .join(' ');

  return (
    <div className="absolute inset-x-0" style={{ top: -travel, bottom: -travel, transform }}>
      {children}
    </div>
  );
}
